import { useCallback, useEffect, useMemo, useState } from 'react';
import { Title } from 'react-admin';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import {
    getPracticeHistoryAnalysis,
    type PracticeHistoryAnalysisData,
    type PracticeHistoryAnalysisQuery,
    type PracticeHistoryExamName,
    type PracticeHistoryUser,
} from '../admin/adminPracticeHistoryAnalysisApi';

type WindowPreset = 'day' | '7d' | '14d' | '30d' | '90d' | 'all' | 'custom';
type ExamFilter = 'All' | PracticeHistoryExamName;
type PaidFilter = 'all' | 'paid' | 'free';

const windowOptions: Array<{ value: WindowPreset; label: string }> = [
    { value: 'day', label: 'Daily' },
    { value: '7d', label: 'Weekly' },
    { value: '14d', label: 'Fortnightly' },
    { value: '30d', label: '30 days' },
    { value: '90d', label: '90 days' },
    { value: 'all', label: 'All time' },
    { value: 'custom', label: 'Custom' },
];

const windowDays: Partial<Record<WindowPreset, number>> = {
    '7d': 7,
    '14d': 14,
    '30d': 30,
    '90d': 90,
};

function toDateInput(date: Date) {
    return date.toISOString().slice(0, 10);
}

function startOfUtcDay(value: string) {
    return new Date(`${value}T00:00:00.000Z`);
}

function addUtcDays(value: string, days: number) {
    const date = startOfUtcDay(value);
    date.setUTCDate(date.getUTCDate() + days);
    return toDateInput(date);
}

function endOfUtcDateRange(value: string) {
    const date = startOfUtcDay(value);
    date.setUTCDate(date.getUTCDate() + 1);
    return date;
}

function parseThresholds(value: string) {
    return [...new Set(
        value
            .split(',')
            .map((item) => Number.parseInt(item.trim(), 10))
            .filter((item) => Number.isInteger(item) && item > 0 && item <= 100_000),
    )].sort((left, right) => left - right).slice(0, 12);
}

function formatNumber(value: number, maximumFractionDigits = 0) {
    return new Intl.NumberFormat(undefined, { maximumFractionDigits }).format(value);
}

function formatDuration(seconds: number) {
    if (seconds <= 0) return '0m';
    const hours = seconds / 3600;
    if (hours >= 1) return `${formatNumber(hours, 1)}h`;
    return `${formatNumber(seconds / 60, 0)}m`;
}

function formatDateTime(value: string) {
    if (!value) return '—';
    return new Intl.DateTimeFormat(undefined, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    }).format(new Date(value));
}

function SummaryCard({ label, value, helper }: { label: string; value: string; helper?: string }) {
    return (
        <Paper
            variant="outlined"
            sx={{
                minWidth: 0,
                p: 2,
                borderRadius: 2,
                background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
            }}
        >
            <Typography sx={{ color: '#64748b', fontSize: '0.74rem', fontWeight: 800, textTransform: 'uppercase' }}>
                {label}
            </Typography>
            <Typography sx={{ mt: 0.5, color: '#0f172a', fontSize: '1.55rem', fontWeight: 850 }}>
                {value}
            </Typography>
            {helper ? <Typography sx={{ mt: 0.25, color: '#94a3b8', fontSize: '0.76rem' }}>{helper}</Typography> : null}
        </Paper>
    );
}

function UserStatusPills({ user }: { user: PracticeHistoryUser }) {
    return (
        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
            <Chip
                size="small"
                label={user.isPaid ? 'Paid' : 'Free'}
                color={user.isPaid ? 'success' : 'default'}
                variant={user.isPaid ? 'filled' : 'outlined'}
            />
            <Chip
                size="small"
                icon={user.emailVerified ? <CheckCircleOutlineIcon /> : undefined}
                label={user.emailVerified ? 'Email verified' : 'Email unverified'}
                color={user.emailVerified ? 'info' : 'default'}
                variant="outlined"
            />
            {user.isDeleted ? <Chip size="small" label="Deleted" color="warning" variant="outlined" /> : null}
        </Stack>
    );
}

function MobileUserCard({ user }: { user: PracticeHistoryUser }) {
    const displayName = [user.firstName, user.lastName].filter(Boolean).join(' ') || 'Unnamed user';
    return (
        <Paper variant="outlined" sx={{ borderRadius: 2, p: 1.75 }}>
            <Typography sx={{ color: '#0f172a', fontWeight: 800 }}>{displayName}</Typography>
            <Typography sx={{ color: '#64748b', fontSize: '0.82rem', overflowWrap: 'anywhere' }}>
                {user.email || 'No email'}
            </Typography>
            <Box sx={{ mt: 1.25 }}>
                <UserStatusPills user={user} />
            </Box>
            <Box sx={{ mt: 1.5, display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
                <Box>
                    <Typography sx={{ color: '#94a3b8', fontSize: '0.7rem', fontWeight: 800 }}>PRACTICES</Typography>
                    <Typography sx={{ fontWeight: 800 }}>{formatNumber(user.practiceCount)}</Typography>
                </Box>
                <Box>
                    <Typography sx={{ color: '#94a3b8', fontSize: '0.7rem', fontWeight: 800 }}>TIME</Typography>
                    <Typography sx={{ fontWeight: 800 }}>{formatDuration(user.practiceTimeSeconds)}</Typography>
                </Box>
                <Box sx={{ gridColumn: '1 / -1' }}>
                    <Typography sx={{ color: '#94a3b8', fontSize: '0.7rem', fontWeight: 800 }}>LAST PRACTICE</Typography>
                    <Typography sx={{ fontSize: '0.82rem' }}>{formatDateTime(user.lastPracticeAtUtc)}</Typography>
                </Box>
            </Box>
        </Paper>
    );
}

export function PracticeHistoryAnalysisPage() {
    const today = useMemo(() => new Date(), []);
    const [windowPreset, setWindowPreset] = useState<WindowPreset>('14d');
    const [selectedDay, setSelectedDay] = useState(() => toDateInput(new Date()));
    const [customFrom, setCustomFrom] = useState(toDateInput(new Date(today.getTime() - 13 * 86_400_000)));
    const [customTo, setCustomTo] = useState(toDateInput(today));
    const [examFilter, setExamFilter] = useState<ExamFilter>('All');
    const [thresholdText, setThresholdText] = useState('20, 30, 50, 100');
    const [thresholds, setThresholds] = useState([20, 30, 50, 100]);
    const [minimumPracticeCount, setMinimumPracticeCount] = useState(0);
    const [paidFilter, setPaidFilter] = useState<PaidFilter>('all');
    const [searchText, setSearchText] = useState('');
    const [search, setSearch] = useState('');
    const [sortBy, setSortBy] = useState<PracticeHistoryAnalysisQuery['sortBy']>('practiceCount');
    const [sortDirection, setSortDirection] = useState<PracticeHistoryAnalysisQuery['sortDirection']>('desc');
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(25);
    const [data, setData] = useState<PracticeHistoryAnalysisData | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const queryWindow = useMemo(() => {
        const to = new Date();
        if (windowPreset === 'all') return { allTime: true, toUtc: to.toISOString() };
        if (windowPreset === 'day') {
            return {
                allTime: false,
                fromUtc: startOfUtcDay(selectedDay).toISOString(),
                toUtc: endOfUtcDateRange(selectedDay).toISOString(),
            };
        }
        if (windowPreset === 'custom') {
            return {
                allTime: false,
                fromUtc: startOfUtcDay(customFrom).toISOString(),
                toUtc: endOfUtcDateRange(customTo).toISOString(),
            };
        }

        const days = windowDays[windowPreset] ?? 14;
        return {
            allTime: false,
            fromUtc: new Date(to.getTime() - days * 86_400_000).toISOString(),
            toUtc: to.toISOString(),
        };
    }, [customFrom, customTo, selectedDay, windowPreset]);

    const load = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await getPracticeHistoryAnalysis({
                ...queryWindow,
                examName: examFilter === 'All' ? undefined : examFilter,
                thresholds,
                minimumPracticeCount,
                isPaid: paidFilter === 'all' ? undefined : paidFilter === 'paid',
                search: search || undefined,
                page: page + 1,
                pageSize,
                sortBy,
                sortDirection,
            });
            setData(result);
        } catch (loadError) {
            setError(loadError instanceof Error ? loadError.message : 'Practice history analysis failed');
        } finally {
            setIsLoading(false);
        }
    }, [
        examFilter,
        minimumPracticeCount,
        page,
        pageSize,
        paidFilter,
        queryWindow,
        search,
        sortBy,
        sortDirection,
        thresholds,
    ]);

    useEffect(() => {
        void load();
    }, [load]);

    const applyThresholds = () => {
        const parsed = parseThresholds(thresholdText);
        if (parsed.length === 0) {
            setError('Enter at least one threshold between 1 and 100,000.');
            return;
        }
        setError(null);
        setThresholds(parsed);
        if (minimumPracticeCount > 0 && !parsed.includes(minimumPracticeCount)) {
            setMinimumPracticeCount(0);
        }
        setPage(0);
    };

    const applySearch = () => {
        setSearch(searchText.trim());
        setPage(0);
    };

    const todayUtc = toDateInput(new Date());
    const selectDay = (day: string) => {
        setSelectedDay(day);
        setWindowPreset('day');
        setPage(0);
    };

    const dateRangeLabel = data?.fromUtc
        ? `${formatDateTime(data.fromUtc)} – ${formatDateTime(data.toUtc)}`
        : `All history through ${formatDateTime(data?.toUtc ?? '')}`;

    return (
        <Box sx={{ p: { xs: 1.25, sm: 2, md: 3 }, minHeight: '100%', minWidth: 0, backgroundColor: '#f8fafc' }}>
            <Title title="Practice History Analysis" />

            <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', md: 'flex-start' }}
                spacing={2}
                sx={{ mb: 2 }}
            >
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h4" sx={{ color: '#0f172a', fontWeight: 850, fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                        Practice History Analysis
                    </Typography>
                    <Typography sx={{ mt: 0.5, color: '#64748b', fontSize: '0.9rem' }}>
                        Cumulative learner practice, time spent, and paid conversion by configurable count threshold.
                    </Typography>
                    <Typography sx={{ mt: 0.35, color: '#94a3b8', fontSize: '0.78rem' }}>
                        {dateRangeLabel}
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                    onClick={() => void load()}
                    disabled={isLoading}
                >
                    Refresh
                </Button>
            </Stack>

            <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, mb: 2, borderRadius: 2, minWidth: 0 }}>
                <Stack spacing={1.5}>
                    <Box sx={{ overflowX: 'auto', pb: 0.25 }}>
                        <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={windowPreset}
                            onChange={(_, value: WindowPreset | null) => {
                                if (value) {
                                    setWindowPreset(value);
                                    setPage(0);
                                }
                            }}
                            aria-label="Practice history time window"
                            sx={{ whiteSpace: 'nowrap' }}
                        >
                            {windowOptions.map((option) => (
                                <ToggleButton key={option.value} value={option.value} sx={{ px: 1.5, textTransform: 'none', fontWeight: 700 }}>
                                    {option.label}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    </Box>

                    {windowPreset === 'day' ? (
                        <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            useFlexGap
                            flexWrap="wrap"
                            aria-label="Daily practice history navigation"
                        >
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<ArrowBackIosNewIcon />}
                                onClick={() => selectDay(addUtcDays(selectedDay, -1))}
                            >
                                Previous
                            </Button>
                            <Button
                                size="small"
                                variant={selectedDay === todayUtc ? 'contained' : 'outlined'}
                                onClick={() => selectDay(todayUtc)}
                            >
                                Today
                            </Button>
                            <Button
                                size="small"
                                variant="outlined"
                                endIcon={<ArrowForwardIosIcon />}
                                onClick={() => selectDay(addUtcDays(selectedDay, 1))}
                                disabled={selectedDay >= todayUtc}
                            >
                                Next
                            </Button>
                            <Typography sx={{ color: '#64748b', fontSize: '0.82rem', fontWeight: 700 }}>
                                {selectedDay} UTC
                            </Typography>
                        </Stack>
                    ) : null}

                    {windowPreset === 'custom' ? (
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                            <TextField
                                label="From (UTC)"
                                type="date"
                                value={customFrom}
                                onChange={(event) => {
                                    setCustomFrom(event.target.value);
                                    setPage(0);
                                }}
                                slotProps={{ inputLabel: { shrink: true } }}
                                fullWidth
                            />
                            <TextField
                                label="To (UTC)"
                                type="date"
                                value={customTo}
                                onChange={(event) => {
                                    setCustomTo(event.target.value);
                                    setPage(0);
                                }}
                                slotProps={{ inputLabel: { shrink: true } }}
                                fullWidth
                            />
                        </Stack>
                    ) : null}

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' },
                            gap: 1.25,
                        }}
                    >
                        <FormControl fullWidth>
                            <InputLabel id="practice-history-exam-label">Exam</InputLabel>
                            <Select
                                labelId="practice-history-exam-label"
                                label="Exam"
                                value={examFilter}
                                onChange={(event) => {
                                    setExamFilter(event.target.value as ExamFilter);
                                    setPage(0);
                                }}
                            >
                                <MenuItem value="All">All exams</MenuItem>
                                <MenuItem value="Academic">Academic</MenuItem>
                                <MenuItem value="Core">Core</MenuItem>
                            </Select>
                        </FormControl>

                        <FormControl fullWidth>
                            <InputLabel id="practice-history-paid-label">Payment</InputLabel>
                            <Select
                                labelId="practice-history-paid-label"
                                label="Payment"
                                value={paidFilter}
                                onChange={(event) => {
                                    setPaidFilter(event.target.value as PaidFilter);
                                    setPage(0);
                                }}
                            >
                                <MenuItem value="all">All users</MenuItem>
                                <MenuItem value="paid">Paid users</MenuItem>
                                <MenuItem value="free">Free users</MenuItem>
                            </Select>
                        </FormControl>

                        <FormControl fullWidth>
                            <InputLabel id="practice-history-minimum-label">Practice count</InputLabel>
                            <Select
                                labelId="practice-history-minimum-label"
                                label="Practice count"
                                value={minimumPracticeCount}
                                onChange={(event) => {
                                    setMinimumPracticeCount(Number(event.target.value));
                                    setPage(0);
                                }}
                            >
                                <MenuItem value={0}>All practiced users</MenuItem>
                                {thresholds.map((threshold) => (
                                    <MenuItem key={threshold} value={threshold}>{`More than ${threshold}`}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth>
                            <InputLabel id="practice-history-sort-label">Sort</InputLabel>
                            <Select
                                labelId="practice-history-sort-label"
                                label="Sort"
                                value={`${sortBy}:${sortDirection}`}
                                onChange={(event) => {
                                    const [nextSortBy, nextDirection] = String(event.target.value).split(':');
                                    setSortBy(nextSortBy as PracticeHistoryAnalysisQuery['sortBy']);
                                    setSortDirection(nextDirection as PracticeHistoryAnalysisQuery['sortDirection']);
                                    setPage(0);
                                }}
                            >
                                <MenuItem value="practiceCount:desc">Most practices</MenuItem>
                                <MenuItem value="practiceCount:asc">Fewest practices</MenuItem>
                                <MenuItem value="practiceTimeSeconds:desc">Most practice time</MenuItem>
                                <MenuItem value="lastPracticeAtUtc:desc">Most recent</MenuItem>
                                <MenuItem value="email:asc">Email A–Z</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>

                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25}>
                        <Stack direction="row" spacing={1} sx={{ flex: 1 }}>
                            <TextField
                                label="Thresholds"
                                value={thresholdText}
                                onChange={(event) => setThresholdText(event.target.value)}
                                helperText="Comma separated, maximum 12"
                                fullWidth
                            />
                            <Button variant="outlined" onClick={applyThresholds} sx={{ alignSelf: 'flex-start', minHeight: 56 }}>
                                Apply
                            </Button>
                        </Stack>
                        <Stack direction="row" spacing={1} sx={{ flex: 1 }}>
                            <TextField
                                label="Search name or email"
                                value={searchText}
                                onChange={(event) => setSearchText(event.target.value)}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter') applySearch();
                                }}
                                fullWidth
                            />
                            <Button variant="outlined" onClick={applySearch} sx={{ minHeight: 56 }}>
                                Search
                            </Button>
                        </Stack>
                    </Stack>
                </Stack>
            </Paper>

            {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(5, minmax(0, 1fr))' },
                    gap: 1.25,
                    mb: 2,
                }}
            >
                <SummaryCard label="Practiced users" value={formatNumber(data?.summary.practicedUsers ?? 0)} />
                <SummaryCard label="Paid users" value={formatNumber(data?.summary.paidUsers ?? 0)} helper="At least one successful payment" />
                <SummaryCard label="Practice attempts" value={formatNumber(data?.summary.practiceAttempts ?? 0)} />
                <SummaryCard label="Practice time" value={formatDuration(data?.summary.practiceTimeSeconds ?? 0)} />
                <SummaryCard label="Average per user" value={formatNumber(data?.summary.averagePracticesPerUser ?? 0, 1)} />
            </Box>

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' },
                    gap: 1.25,
                    mb: 2,
                }}
            >
                {data?.thresholdAnalysis.map((threshold) => {
                    const paidRate = threshold.userCount > 0 ? (threshold.paidUserCount / threshold.userCount) * 100 : 0;
                    const selected = minimumPracticeCount === threshold.threshold;
                    return (
                        <Paper
                            key={threshold.threshold}
                            component="button"
                            type="button"
                            onClick={() => {
                                setMinimumPracticeCount(threshold.threshold);
                                setPage(0);
                            }}
                            variant="outlined"
                            sx={{
                                p: 2,
                                borderRadius: 2,
                                cursor: 'pointer',
                                textAlign: 'left',
                                backgroundColor: selected ? alpha('#0284c7', 0.08) : '#ffffff',
                                borderColor: selected ? '#0284c7' : '#e2e8f0',
                                '&:hover': { borderColor: '#38bdf8' },
                            }}
                        >
                            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                                <Box>
                                    <Typography sx={{ color: '#64748b', fontSize: '0.74rem', fontWeight: 800, textTransform: 'uppercase' }}>
                                        More than {threshold.threshold}
                                    </Typography>
                                    <Typography sx={{ mt: 0.4, color: '#0f172a', fontSize: '1.6rem', fontWeight: 850 }}>
                                        {formatNumber(threshold.userCount)}
                                    </Typography>
                                </Box>
                                <Chip
                                    color="success"
                                    label={`${formatNumber(threshold.paidUserCount)} paid`}
                                    sx={{ fontWeight: 800 }}
                                />
                            </Stack>
                            <Typography sx={{ mt: 1, color: '#64748b', fontSize: '0.78rem' }}>
                                {formatNumber(paidRate, 1)}% paid conversion
                            </Typography>
                        </Paper>
                    );
                })}
            </Box>

            <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', minWidth: 0 }}>
                <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #e2e8f0' }}>
                    <Typography sx={{ color: '#0f172a', fontWeight: 850 }}>
                        Users
                    </Typography>
                    <Typography sx={{ color: '#64748b', fontSize: '0.78rem' }}>
                        {formatNumber(data?.users.totalCount ?? 0)} users match the current detail filters
                    </Typography>
                </Box>

                <Stack spacing={1} sx={{ display: { xs: 'flex', md: 'none' }, p: 1.25 }}>
                    {data?.users.items.map((user) => <MobileUserCard key={user.userId} user={user} />)}
                    {!isLoading && data?.users.items.length === 0 ? (
                        <Typography sx={{ py: 4, textAlign: 'center', color: '#64748b' }}>No matching users</Typography>
                    ) : null}
                </Stack>

                <TableContainer sx={{ display: { xs: 'none', md: 'block' }, maxHeight: '60vh' }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ minWidth: 180, fontWeight: 800 }}>User</TableCell>
                                <TableCell sx={{ minWidth: 220, fontWeight: 800 }}>Email</TableCell>
                                <TableCell sx={{ minWidth: 190, fontWeight: 800 }}>Status</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 800 }}>Practices</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 800 }}>Time</TableCell>
                                <TableCell sx={{ minWidth: 170, fontWeight: 800 }}>First practice</TableCell>
                                <TableCell sx={{ minWidth: 170, fontWeight: 800 }}>Last practice</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data?.users.items.map((user) => (
                                <TableRow key={user.userId} hover>
                                    <TableCell>{[user.firstName, user.lastName].filter(Boolean).join(' ') || 'Unnamed user'}</TableCell>
                                    <TableCell sx={{ overflowWrap: 'anywhere' }}>{user.email || '—'}</TableCell>
                                    <TableCell><UserStatusPills user={user} /></TableCell>
                                    <TableCell align="right">{formatNumber(user.practiceCount)}</TableCell>
                                    <TableCell align="right">{formatDuration(user.practiceTimeSeconds)}</TableCell>
                                    <TableCell>{formatDateTime(user.firstPracticeAtUtc)}</TableCell>
                                    <TableCell>{formatDateTime(user.lastPracticeAtUtc)}</TableCell>
                                </TableRow>
                            ))}
                            {!isLoading && data?.users.items.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7}>
                                        <Typography sx={{ py: 4, textAlign: 'center', color: '#64748b' }}>No matching users</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : null}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    count={data?.users.totalCount ?? 0}
                    page={page}
                    onPageChange={(_, nextPage) => setPage(nextPage)}
                    rowsPerPage={pageSize}
                    onRowsPerPageChange={(event) => {
                        setPageSize(Number(event.target.value));
                        setPage(0);
                    }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                />
            </Paper>

            <Typography sx={{ mt: 1.5, color: '#94a3b8', fontSize: '0.75rem' }}>
                Practice counts include all stored practice-attempt rows in the selected window. Paid users have at least one succeeded payment.
            </Typography>
        </Box>
    );
}
